import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export type TransactionType = 'income' | 'expense' | 'transfer';
export type TransactionStatus = 'pending' | 'categorized' | 'reviewed' | 'reconciled';

export interface PersonalTransaction {
  id: string;
  user_id: string;
  context_type: 'personal';
  personal_category_id: string | null;
  transaction_date: string;
  transaction_type: TransactionType;
  description: string;
  reference_number: string | null;
  amount: string;
  source_type: string;
  status: TransactionStatus;
  receipt_notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TransactionWithCategory extends PersonalTransaction {
  category_name: string | null;
  category_icon: string | null;
  category_color: string | null;
}

export interface CreateTransactionInput {
  transaction_date: string;
  transaction_type: TransactionType;
  description: string;
  amount: number;
  personal_category_id?: string;
  reference_number?: string;
  receipt_notes?: string;
}

export interface UpdateTransactionInput {
  transaction_date?: string;
  transaction_type?: TransactionType;
  description?: string;
  amount?: number;
  personal_category_id?: string | null;
  reference_number?: string | null;
  receipt_notes?: string | null;
}

export interface TransactionFilters {
  dateFrom?: string;
  dateTo?: string;
  categoryId?: string;
  type?: TransactionType;
  search?: string;
  limit?: number;
  offset?: number;
}

export function usePersonalTransactions(filters?: TransactionFilters) {
  const { user, session, initialized } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (filters?.dateFrom) queryParams.set('dateFrom', filters.dateFrom);
  if (filters?.dateTo) queryParams.set('dateTo', filters.dateTo);
  if (filters?.categoryId) queryParams.set('categoryId', filters.categoryId);
  if (filters?.type) queryParams.set('type', filters.type);
  if (filters?.search) queryParams.set('search', filters.search);
  if (filters?.limit) queryParams.set('limit', String(filters.limit));
  if (filters?.offset) queryParams.set('offset', String(filters.offset));
  const queryString = queryParams.toString();
  const endpoint = `/personal/transactions${queryString ? `?${queryString}` : ''}`;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'personalTransactions',
      user?.id,
      filters?.dateFrom,
      filters?.dateTo,
      filters?.categoryId,
      filters?.type,
      filters?.search,
      filters?.limit,
      filters?.offset,
    ],
    queryFn: async (): Promise<{ transactions: TransactionWithCategory[]; total: number }> => {
      if (!session?.access_token) {
        return { transactions: [], total: 0 };
      }

      const response = await api.get<{ transactions: TransactionWithCategory[]; total: number }>(
        endpoint,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch transactions');
      }

      return response.data ?? { transactions: [], total: 0 };
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['personalTransactions'] });
    // Also invalidate budgets since spent amounts may have changed
    queryClient.invalidateQueries({ queryKey: ['personalBudgets'] });
  };

  const createTransaction = useMutation({
    mutationFn: async (input: CreateTransactionInput) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ transaction: PersonalTransaction }>(
        '/personal/transactions',
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to create transaction');
      return response.data!.transaction;
    },
    onSuccess: invalidateAll,
  });

  const updateTransaction = useMutation({
    mutationFn: async ({ id, ...input }: UpdateTransactionInput & { id: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ transaction: PersonalTransaction }>(
        `/personal/transactions/${id}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update transaction');
      return response.data!.transaction;
    },
    onSuccess: invalidateAll,
  });

  const deleteTransaction = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/transactions/${id}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete transaction');
      return response.data!;
    },
    onSuccess: invalidateAll,
  });

  return {
    transactions: data?.transactions ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
    refetch,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  };
}
